<template>
  <div class="w-full max-w-5xl mx-auto px-4 py-8" data-testid="ai-agents-module">
    <div class="mb-8 text-center">
      <div class="inline-flex text-zinc-400 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-700 p-4 rounded-full mb-5">
        <Icon name="ai.robot" size="lg" />
      </div>
      <h1 class="mb-3 font-bold text-3xl text-zinc-900 dark:text-zinc-100">Agents</h1>
      <p class="max-w-2xl mx-auto text-lg text-zinc-600 dark:text-zinc-300">Automate repeatable work with purpose-built Zight agents.</p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="agents-catalog">
      <article
        class="flex flex-col overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-sm"
        data-testid="zight-playbooks-card"
      >
        <div class="flex flex-col flex-1 p-6">
          <div class="flex items-start justify-between gap-4 mb-4">
            <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">
              <Icon name="general.stars" size="lg" />
            </div>
            <span class="rounded-full bg-blue-50 dark:bg-blue-900/30 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-200"
              >Alpha</span
            >
          </div>

          <h2 class="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">Zight Playbooks</h2>
          <p class="text-zinc-600 dark:text-zinc-300 mb-5">
            Record a browser workflow once and turn it into a reusable playbook for onboarding, support, and recurring tasks.
          </p>

          <iframe
            v-if="dashboardAgentsVideoUrl"
            :src="dashboardAgentsVideoUrl"
            title="Zight Playbooks overview"
            allowtransparency="true"
            allowfullscreen
            style="border: medium none"
            frameborder="0"
            class="w-full rounded-lg aspect-video mb-5"
          />

          <div class="space-y-3 mb-6 text-sm text-zinc-600 dark:text-zinc-300">
            <p>
              <strong class="font-semibold text-zinc-800 dark:text-zinc-100">Create with Chrome.</strong> The Zight Chrome extension records the browser
              workflow used to build a playbook.
            </p>
            <p>
              <strong class="font-semibold text-zinc-800 dark:text-zinc-100">Run with your AI agent.</strong> Supported AI clients can use Zight MCP to run a
              playbook in a browser they control—without the Zight Agent Runner extension.
            </p>
          </div>

          <div class="mt-auto">
            <Button data-testid="ai-agents-interest-button" type="solid" variant="blue" target="_blank" :href="typeformUrl" @click="handleJoinWaitlist">
              Request access
            </Button>
          </div>
        </div>
      </article>

      <article
        class="flex flex-col rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-6 shadow-sm"
        data-testid="help-center-agent-card"
      >
        <div class="flex items-start justify-between gap-4 mb-4">
          <div class="p-3 rounded-lg bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-300">
            <Icon name="general.file" size="lg" />
          </div>
          <span class="rounded-full bg-zinc-100 dark:bg-zinc-700 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300"
            >Coming soon</span
          >
        </div>

        <h2 class="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">Zight Help Center Agent</h2>
        <p class="text-zinc-600 dark:text-zinc-300 mb-5">
          Turn product videos into drafted help center updates, so documentation can keep pace as your product changes.
        </p>
        <p class="mt-auto text-sm text-zinc-500 dark:text-zinc-400">This agent is not yet available.</p>
      </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Button, Icon } from '@/storybook';
import { getRequest } from '@/ApiHelpers';
import { useTrack } from '@/composables/useTrack';
import { useUserStore } from '@/store/useUserStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';

const dashboardAgentsVideoUrl = ref('');

const { track } = useTrack();
const userStore = useUserStore();
const organizationStore = useOrganizationStore();
const { id: userId, email } = storeToRefs(userStore);
const { id: orgId, ownerId, planCode } = storeToRefs(organizationStore);

onMounted(async () => {
  track('dashboard_agents_viewed', {
    user_id: userId.value,
    email: email.value,
    org_id: orgId.value,
  });

  try {
    const response = await getRequest('/api/v5/dropper_configurations/dashboard_agents_video_url');
    const content = response?.data?.dropperConfiguration?.attributes?.content;
    if (content) {
      dashboardAgentsVideoUrl.value = content;
    }
  } catch {
    // Config not set yet, video embed stays hidden
  }
});

const handleJoinWaitlist = () => {
  track('feature_access_request', {
    feature_name: 'ai-agents',
    user_id: userId.value,
    email: email.value,
    org_id: orgId.value,
    owner_id: ownerId.value,
    plan: planCode.value,
  });
};

const typeformUrl = computed(() => {
  const base = 'https://form.typeform.com/to/NQIKYtnA?typeform-source=share.zight.com';
  if (email.value) {
    return `${base}&email=${encodeURIComponent(email.value)}`;
  }
  return base;
});
</script>
